import { toPlainText } from './markdown';
import type { LocalNote } from './note';
import { tagKey } from './tags';

/** Lowercases and removes diacritics so that "Küche" matches "kuche". */
export function normalize(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('de');
}

const cache = new WeakMap<LocalNote, string>();

function haystack(note: LocalNote): string {
  let h = cache.get(note);
  if (h === undefined) {
    h = normalize(`${note.title}\n${toPlainText(note.content)}\n${note.tags.map((t) => `#${t}`).join(' ')}`);
    cache.set(note, h);
  }
  return h;
}

export interface Query {
  terms: string[];
  tags: string[];
}

/** Parses a search query; "#tag" terms filter by tag prefix, others are substring terms. */
export function parseQuery(input: string): Query {
  const q: Query = { terms: [], tags: [] };
  for (const raw of input.trim().split(/\s+/)) {
    if (!raw) continue;
    if (raw.startsWith('#') && raw.length > 1) q.tags.push(tagKey(raw.slice(1)));
    else q.terms.push(normalize(raw));
  }
  return q;
}

/** Whether a note matches all terms and tags of the query. */
export function matches(note: LocalNote, query: Query): boolean {
  if (query.tags.length > 0) {
    const keys = note.tags.map(tagKey);
    const ok = query.tags.every((t) => keys.some((k) => k.startsWith(t) || k.split(':').some((s) => s.startsWith(t))));
    if (!ok) return false;
  }
  if (query.terms.length === 0) return true;
  const h = haystack(note);
  return query.terms.every((t) => h.includes(t));
}

/** Filters notes; title hits are ranked first. Input order is otherwise preserved. */
export function search(notes: readonly LocalNote[], input: string): LocalNote[] {
  const query = parseQuery(input);
  if (query.terms.length === 0 && query.tags.length === 0) return [...notes];
  const hits = notes.filter((n) => matches(n, query));
  const inTitle = (n: LocalNote) => query.terms.length > 0 && query.terms.every((t) => normalize(n.title).includes(t));
  return [...hits.filter(inTitle), ...hits.filter((n) => !inTitle(n))];
}
